import { useEffect, useRef, useState } from "react";
import { Reveal } from "../landing/Reveal";
import { useCart } from "../../contexts/CartContext";

const MEALS = [
  { emoji: "🍔", name: "Smash Burger", chef: "@priyacooks", price: 89, rating: "4.9", time: "15 min" },
  { emoji: "🍜", name: "Midnight Ramen", chef: "@kenjibowl", price: 79, rating: "4.8", time: "20 min" },
  { emoji: "🌮", name: "Abuela Tacos", chef: "@leomakes", price: 99, rating: "5.0", time: "12 min" },
];

const FoodReel = () => {
  const videoRef = useRef(null);
  const [videoOk, setVideoOk] = useState(true);
  const { add } = useCart();

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !videoOk) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.play().catch(() => {});
        else el.pause();
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [videoOk]);

  return (
    <div className="relative mx-auto h-[480px] w-[270px] overflow-hidden rounded-[2.2rem] border border-white/10 bg-gradient-to-b from-[#F97316] via-[#8a4b2d] to-[#09090B] sm:w-[290px]" data-testid="foodbite-reel">
      {videoOk ? (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setVideoOk(false)}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/videos/reel-food.webm" type="video/webm" />
          <source src="/videos/reel-food.mp4" type="video/mp4" />
        </video>
      ) : (
        <span className="absolute left-1/2 top-1/4 -translate-x-1/2 text-8xl drop-shadow-2xl">🍜</span>
      )}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/10" />
      <div className="absolute right-3 top-1/3 flex flex-col items-center gap-5 text-white">
        <span className="flex flex-col items-center gap-1 text-lg">❤️<span className="font-jbmono text-[9px]">12.4K</span></span>
        <span className="flex flex-col items-center gap-1 text-lg">💬<span className="font-jbmono text-[9px]">318</span></span>
        <span className="flex flex-col items-center gap-1 text-lg">👨‍🍳<span className="font-jbmono text-[9px]">Follow</span></span>
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-5 pt-12">
        <p className="font-display text-lg font-bold text-white">Midnight Ramen Drop</p>
        <p className="font-jbmono text-[10px] text-slate-300">@kenjibowl · Hostel Block C</p>
        <button
          data-testid="foodbite-order-cta"
          onClick={() => add({ emoji: "🍜", name: "Midnight Ramen", price: 79 })}
          className="mt-3 w-full rounded-full bg-[#F97316] py-3 text-xs font-bold uppercase tracking-wider text-white transition-transform hover:scale-[1.02]"
        >
          Add to Order · ₹79
        </button>
      </div>
    </div>
  );
};

export const FoodBite = () => {
  const { add } = useCart();
  return (
    <section id="foodbite" data-testid="foodbite-section" className="relative overflow-hidden py-20 sm:py-24">
      <div aria-hidden="true" className="absolute -left-32 top-1/3 h-[380px] w-[380px] rounded-full bg-[#F97316]/12 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#F97316]">🍔 FoodBite</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Watch the reel. Order the meal.
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
              Uber Eats speed with TikTok discovery. Student chefs cook, you scroll,
              tap, eat — in under 20 minutes.
            </p>
            <div className="mt-8 space-y-3">
              {MEALS.map((m) => (
                <div
                  key={m.name}
                  data-testid={`meal-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl transition-colors hover:border-[#F97316]/50"
                >
                  <span className="text-3xl transition-transform duration-300 group-hover:scale-125">{m.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white">{m.name}</p>
                    <p className="font-jbmono text-[10px] text-slate-500">
                      {m.chef} · ⭐ {m.rating} · {m.time}
                    </p>
                  </div>
                  <button
                    data-testid={`meal-add-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => add(m)}
                    className="rounded-full bg-[#F97316] px-4 py-2 font-jbmono text-xs font-bold text-white transition-transform hover:scale-105"
                  >
                    ₹{m.price} +
                  </button>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <FoodReel />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
